import { HttpClient, HttpErrorResponse, HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { AppConstant } from '@app/app.constant';
import { decodeJWT } from '@app/core/common/common.utils';
import { HTTP_STATUS_CODE } from '@core/common/http-status-code';
import { IS_PUBLIC_API, TOKEN_REFRESHED } from '@core/models/api-option.interface';
import { GlobalService } from '@core/services/global.service';
import { AuthService } from '@modules/auth/auth.service';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';

/**
 * Ported from the agent portal. The Id server owns the session and its refresh token; this app only
 * ever asks Id for the session's CURRENT access token. Holding a refresh token of our own is what
 * this design replaces: the backend rotates it single-use, so two holders sign each other out.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService implements OnDestroy {
    private static readonly SESSION_TOKEN_PATH = '/session/access-token';

    static readonly EXCLUDING_URL_LIST: Array<{ url: string; method?: string }> = [
        // A 401 from the refresh endpoint itself means the session is over. Refreshing in response
        // to it would just ask the same dead session again.
        { url: TokenRefreshService.SESSION_TOKEN_PATH },
    ];

    private static readonly EXPIRY_BUFFER_SECONDS = 120;

    currentToken: string = null;
    private isForcingLogout = false;
    private refresh$: Observable<string> | null = null;
    private readonly destroy$ = new Subject<void>();

    private readonly http: HttpClient = inject(HttpClient);
    private readonly authService: AuthService = inject(AuthService);
    private readonly globalService: GlobalService = inject(GlobalService);

    constructor() {
        this.globalService.storage
            .watch(AppConstant.GLOBAL_STORAGE.TOKEN)
            .pipe(takeUntil(this.destroy$))
            .subscribe((token) => {
                this.currentToken = token;
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    static updateToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
        // TOKEN_REFRESHED marks the retried request so a second 401 propagates instead of starting
        // another refresh cycle — without it, a persistently rejected session refreshes forever.
        const context = request.context.set(TOKEN_REFRESHED, true);
        return request.clone({ setHeaders: { Authorization: `Bearer ${token}` }, context });
    }

    isIgnoredRequest(request: HttpRequest<unknown>): boolean {
        for (const item of TokenRefreshService.EXCLUDING_URL_LIST) {
            const { url, method } = item;
            if (request.url.endsWith(url)) {
                return !method || request.method === method;
            }
        }

        return false;
    }

    isReadyToRefreshProactively(request: HttpRequest<unknown>): boolean {
        return (
            !!this.currentToken &&
            this._isTokenNearExpiry() &&
            !this.isIgnoredRequest(request) &&
            !request.context.get(IS_PUBLIC_API)
        );
    }

    /**
     * Ask the Id server for the session's current access token.
     *
     * `withCredentials` because the session is a cookie on the Id origin; no `ILI` header so
     * AuthInterceptor leaves it alone. Only the access token comes back: Id keeps the refresh
     * token, which is the point. The Id origin must list this app in ORIGINS_WHITELIST or this
     * call dies as a CORS error that reads like the session vanished.
     */
    private performRefresh(): Observable<string> {
        const url = `${environment.redocConnect.issuer}${TokenRefreshService.SESSION_TOKEN_PATH}`;
        return this.http.get<{ data?: { accessToken?: string } }>(url, { withCredentials: true }).pipe(
            map((response) => {
                const accessToken = response?.data?.accessToken;
                if (!accessToken) {
                    // A 200 with no token would otherwise store `undefined` and 401 on every
                    // subsequent request without ever explaining why.
                    throw new HttpErrorResponse({
                        status: HTTP_STATUS_CODE.UNAUTHORIZED,
                        statusText: 'The Id server returned no access token.',
                    });
                }
                this.authService.setAccessToken(accessToken);
                return accessToken;
            }),
            shareReplay({ bufferSize: 1, refCount: false }),
        );
    }

    doRefresh(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this.isForcingLogout) {
            return throwError(() => new HttpErrorResponse({ status: HTTP_STATUS_CODE.UNAUTHORIZED }));
        }
        if (!this.refresh$) {
            this.refresh$ = this.performRefresh().pipe(
                catchError((err) => {
                    this.refresh$ = null;
                    this._triggerForcedLogout();
                    return throwError(() => err);
                }),
                // Reset on `next` (not `complete`): a caller may synchronously start a new cycle
                // from inside the retried request's subscriber, before `complete` is delivered.
                tap({ next: () => (this.refresh$ = null) }),
            );
        }
        return this.refresh$.pipe(
            take(1),
            switchMap((token) => next.handle(TokenRefreshService.updateToken(request, token))),
        );
    }

    /**
     * Always worth trying: only Id can say whether the session is still alive, and it says so with
     * a 401 on the refresh call — which `doRefresh` already turns into a forced logout.
     */
    handle401(request: HttpRequest<any>, next: HttpHandler, error: HttpErrorResponse): Observable<HttpEvent<any>> {
        if (this.isForcingLogout) {
            throw error;
        }
        return this.doRefresh(request, next);
    }

    private _isTokenNearExpiry(): boolean {
        if (!this.currentToken) return false;

        const decoded = decodeJWT(this.currentToken);

        if (!decoded || !decoded.exp) return false;

        return decoded.exp - Math.floor(Date.now() / 1000) < TokenRefreshService.EXPIRY_BUFFER_SECONDS;
    }

    private _triggerForcedLogout(): void {
        if (this.isForcingLogout) return;

        this.isForcingLogout = true;

        setTimeout(() => {
            this.authService.logout();
            this.authService.redirectToLogin();
            this.isForcingLogout = false;
        }, 1000);
    }
}
